'use client'

import * as React from 'react'

import { appIconStyle } from '../app-icon-style'
import { cn } from '../cn'
import { type AppBrandState, useAppBrand } from '../hooks/use-app-brand'
import { useTileFavicon } from '../hooks/use-app-favicon'
import { type AppBrand, type AppBrandKey, getAppBrand } from '../logos'
import { SIDEBAR_TILE_COLORS } from './app-sidebar'
import { Card } from './card'
import { PhosphorIcon } from './icon-picker'
import { Skeleton } from './skeleton'

export interface SigninCardProps {
  appKey: AppBrandKey
  /**
   * href the "Sign in with Google" button points at — typically `/api/auth/login`.
   * The current `pathname + search` is appended as `?next=…` so the user
   * lands back where they came from after the multipass round-trip.
   */
  loginHref: string
  /** Optional override for the bottom domains line. */
  allowedDomainsLine?: string
  /** Optional className appended to the outermost wrapper. */
  className?: string
  /**
   * Optional error message rendered above the Google button — e.g. when
   * the page is reached via `/signin?error=denied`. Caller is responsible
   * for parsing the query and passing the human-readable string in.
   */
  errorMessage?: string | null
  /**
   * Optional content rendered between the Google button and the
   * allowed-domains line — e.g. a magic-link form. Most apps don't need
   * this; Road Warrior uses it for its email/magic-link path.
   */
  extras?: React.ReactNode
  /**
   * Point the tab favicon at the app's live registry tile. Defaults to true —
   * the card IS the sign-in page. Pass false when several cards share a page
   * (a gallery) so they don't fight over the one favicon.
   */
  favicon?: boolean
}

/**
 * Single canonical signin layout for every Ordo app. Renders the Factory-
 * style card — colored brand tile + app name + "Sign in to continue" +
 * Google button + allowed-domains line.
 *
 * The tile, name and tab favicon are the app's LIVE registry tile (icon +
 * colors set in HQ → Apps, read by `registrySlug` through `useAppBrand`), so
 * they match the app's sidebar and HQ's launcher with no deploy. The static
 * `APP_BRANDS[appKey]` brand is the fallback when that lookup fails, and the
 * only source for apps whose `registrySlug` is null.
 */
export function SigninCard({
  appKey,
  loginHref,
  allowedDomainsLine,
  className,
  errorMessage,
  extras,
  favicon = true,
}: SigninCardProps) {
  const brand = getAppBrand(appKey)
  const href = useNextAwareHref(loginHref)
  const live = useAppBrand(brand?.registrySlug ?? null)
  const liveBrand = live.status === 'ready' ? live.brand : null
  useTileFavicon(
    favicon && liveBrand ? { iconName: liveBrand.icon, bgColor: liveBrand.bgColor, fgColor: liveBrand.fgColor } : null,
  )
  if (!brand) {
    return null
  }

  return (
    <div className={cn('flex min-h-screen items-center justify-center bg-background p-4', className)}>
      <Card className="flex w-full max-w-sm flex-col items-center gap-6 p-8 shadow-md">
        <SigninBrandTile brand={brand} live={live} />
        <div className="flex flex-col items-center text-center">
          {live.status === 'loading' ? (
            <Skeleton className="h-8 w-28" />
          ) : (
            <h1 className="text-2xl font-semibold tracking-tight">{liveBrand?.name ?? brand.name}</h1>
          )}
          <p className="mt-1 text-sm text-muted-foreground">Sign in to continue</p>
        </div>
        {errorMessage ? (
          <div className="w-full rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            {errorMessage}
          </div>
        ) : null}
        <a
          href={href}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
          <GoogleGlyph className="h-4 w-4" />
          Sign in with Google
        </a>
        <p className="text-xs text-muted-foreground">{allowedDomainsLine ?? brand.allowedDomainsLine}</p>
        {extras ? <div className="w-full">{extras}</div> : null}
      </Card>
    </div>
  )
}

/**
 * The live registry tile (25%-rounded, like the launcher's), a skeleton while
 * the first lookup is in flight, else the static brand tile.
 */
function SigninBrandTile({ brand, live }: { brand: AppBrand; live: AppBrandState }) {
  const Logo = brand.Logo
  if (live.status === 'loading') {
    return <Skeleton className="h-12 w-12 rounded-xl" />
  }
  if (live.status === 'ready') {
    return (
      <div
        className="grid h-12 w-12 place-items-center rounded-xl"
        style={appIconStyle(live.brand.bgColor, live.brand.fgColor)}>
        <PhosphorIcon name={live.brand.icon} className="h-6 w-6" fallback={<Logo className="h-6 w-6" />} />
      </div>
    )
  }
  const tileClass = SIDEBAR_TILE_COLORS[brand.colorKey] ?? SIDEBAR_TILE_COLORS['gray']
  return (
    <div className={cn('grid h-12 w-12 place-items-center rounded-md', tileClass)}>
      <Logo className="h-6 w-6" />
    </div>
  )
}

function useNextAwareHref(loginHref: string): string {
  const [href, setHref] = React.useState(loginHref)
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const current = `${window.location.pathname}${window.location.search}`
    if (!current || current === '/signin' || current === '/login' || current === '/') {
      setHref(loginHref)
      return
    }
    const sep = loginHref.includes('?') ? '&' : '?'
    setHref(`${loginHref}${sep}next=${encodeURIComponent(current)}`)
  }, [loginHref])
  return href
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path d="M5.84 14.1a6.97 6.97 0 0 1 0-4.2V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05" />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  )
}
